import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Building2, User, Mail, Phone, MapPin, Globe, 
  FileText, Send, Loader2, Check, Instagram,
  Twitter, Youtube, Music, ArrowRight, Type, List, ToggleLeft
} from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import api, { settingsApi } from '../lib/api';

const ProducerApplicationPage = () => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formConfig, setFormConfig] = useState(null);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [formData, setFormData] = useState({});

  // Load form configuration on mount
  useEffect(() => {
    const loadFormConfig = async () => {
      try {
        const response = await settingsApi.getProducerForm();
        if (response.data && response.data.fields) {
          setFormConfig(response.data);
          // Initialize form data with empty values for each field
          const initialData = {};
          response.data.fields.forEach(field => {
            // Use field.id as the key since that's what the backend provides
            const fieldKey = field.id || field.name;
            initialData[fieldKey] = field.type === 'checkbox' ? false : '';
          });
          setFormData(initialData);
        }
      } catch (error) {
        console.error('Error loading form config:', error);
        // If no custom form exists, form will show default message
      } finally {
        setLoadingConfig(false);
      }
    };
    loadFormConfig();
  }, []);

  // Get hero content with defaults
  const heroContent = {
    badge: formConfig?.hero?.badge || 'Portal de Productoras',
    title: formConfig?.hero?.title || 'Vendé tus entradas con nosotros',
    subtitle: formConfig?.hero?.subtitle || 'Registrá tu productora y accedé a nuestra plataforma de venta de entradas, estadísticas en tiempo real y una comunidad de compradores activos.'
  };

  // Get benefits with defaults
  const benefits = formConfig?.benefits || [
    { title: 'Gestión Completa', description: 'Panel propio para crear y gestionar eventos' },
    { title: 'Estadísticas', description: 'Métricas de ventas y comunidad en tiempo real' },
    { title: 'Tu Marca', description: 'Personalización con tu logo, colores y dominio' }
  ];

  const showBenefits = formConfig?.showBenefits !== false;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
  };

  // Helper to find field value by label patterns
  const findFieldValue = (patterns) => {
    if (!formConfig?.fields) return '';
    for (const field of formConfig.fields) {
      const fieldKey = field.id || field.name;
      const labelLower = field.label?.toLowerCase() || '';
      if (patterns.some(p => labelLower.includes(p.toLowerCase()))) {
        return formData[fieldKey] || '';
      }
    }
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields using field.id
    if (formConfig?.fields) {
      const missingRequired = formConfig.fields.filter(field => {
        const fieldKey = field.id || field.name;
        return field.required && !formData[fieldKey];
      });
      if (missingRequired.length > 0) {
        toast.error(`Completá los campos requeridos: ${missingRequired.map(f => f.label).join(', ')}`);
        return;
      }
    }
    
    setLoading(true);

    try {
      // Transform form data to include field labels for admin review
      const applicationData = {
        form_responses: Object.entries(formData).map(([key, value]) => {
          const field = formConfig?.fields?.find(f => (f.id || f.name) === key);
          return {
            field_id: key,
            field_label: field?.label || key,
            field_type: field?.type || 'text',
            value: value
          };
        }),
        // Extract key fields for the producer record - check by label since IDs are auto-generated
        business_name: findFieldValue(['nombre de la productora', 'nombre', 'business_name', 'name']) || 'Sin nombre',
        email: findFieldValue(['email', 'correo', 'e-mail']) || null,
        phone: findFieldValue(['teléfono', 'telefono', 'phone', 'celular']) || '',
        contact_name: findFieldValue(['nombre contacto', 'responsable', 'contact_name']) || '',
        description: findFieldValue(['descripción', 'descripcion', 'description']) || ''
      };
      
      await api.post('/producers/apply', applicationData);
      setSubmitted(true);
      toast.success('¡Solicitud enviada correctamente!');
    } catch (error) {
      console.error('Error:', error);
      // Handle validation errors (array of error objects)
      let errorMessage = 'Error al enviar la solicitud';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (Array.isArray(detail)) {
          errorMessage = detail.map(e => e.msg || e).join(', ');
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        }
      }
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getFieldIcon = (type) => {
    switch (type) {
      case 'email': return Mail;
      case 'phone': return Phone;
      case 'textarea': return FileText;
      case 'select': return List;
      case 'checkbox': return ToggleLeft;
      default: return Type;
    }
  };

  const renderField = (field) => {
    const Icon = getFieldIcon(field.type);
    const fieldKey = field.id || field.name;
    
    switch (field.type) {
      case 'textarea':
        return (
          <div key={fieldKey}>
            <label className="block text-sm text-zinc-400 mb-1">
              {field.label} {field.required && '*'}
            </label>
            <textarea
              name={fieldKey}
              value={formData[fieldKey] || ''}
              onChange={handleChange}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:border-[#D4AF37] focus:outline-none"
              rows={3}
              placeholder={field.placeholder || ''}
              required={field.required}
            />
          </div>
        );
      
      case 'select':
        return (
          <div key={fieldKey}>
            <label className="block text-sm text-zinc-400 mb-1">
              {field.label} {field.required && '*'}
            </label>
            <select
              name={fieldKey}
              value={formData[fieldKey] || ''}
              onChange={handleChange}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] focus:outline-none"
              required={field.required}
            >
              <option value="">Seleccionar...</option>
              {field.options?.map((opt, i) => (
                <option key={i} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
        );
      
      case 'checkbox':
        return (
          <div key={fieldKey} className="flex items-start gap-3">
            <input
              type="checkbox"
              name={fieldKey}
              checked={formData[fieldKey] || false}
              onChange={handleChange}
              className="mt-1 w-4 h-4 rounded border-zinc-700 bg-zinc-800 text-[#D4AF37] focus:ring-[#D4AF37]"
              required={field.required}
            />
            <label className="text-sm text-zinc-400">
              {field.label} {field.required && '*'}
            </label>
          </div>
        );
      
      default:
        return (
          <div key={fieldKey}>
            <label className="block text-sm text-zinc-400 mb-1">
              {field.label} {field.required && '*'}
            </label>
            <div className="relative">
              <Icon className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
              <input
                type={field.type === 'email' ? 'email' : field.type === 'phone' ? 'tel' : 'text'}
                name={fieldKey}
                value={formData[fieldKey] || ''}
                onChange={handleChange}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg pl-10 pr-4 py-3 text-white placeholder-zinc-500 focus:border-[#D4AF37] focus:outline-none"
                placeholder={field.placeholder || ''}
                required={field.required}
              />
            </div>
          </div>
        );
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#0A0A0A]">
        <SEO title="Solicitud Enviada - Productoras" />
        <Navbar />
        <div className="pt-32 pb-20 px-4 flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center max-w-md"
          >
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="text-green-500" size={40} />
            </div>
            <h1 className="text-3xl font-black text-white mb-4">¡Solicitud Enviada!</h1>
            <p className="text-zinc-400 mb-8">
              Gracias por tu interés en ser parte de nuestra plataforma. 
              Revisaremos tu solicitud y te contactaremos pronto.
            </p>
            <Button
              onClick={() => window.location.href = '/'}
              className="bg-[#D4AF37] hover:bg-[#B4941F] text-black font-bold"
            >
              Volver al inicio
            </Button>
          </motion.div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <SEO 
        title="Registrá tu Productora - BRAVX"
        description="Unite a nuestra plataforma y vendé entradas para tus eventos"
      />
      <Navbar />

      {/* Hero */}
      <section className="pt-28 pb-12 px-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#D4AF37]/5 to-transparent" />
        <div className="max-w-3xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="inline-flex items-center gap-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full px-4 py-2 mb-6">
              <Building2 className="text-[#D4AF37]" size={16} />
              <span className="text-[#D4AF37] text-sm font-medium">{heroContent.badge}</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-black text-white mb-4">
              {heroContent.title}
            </h1>
            <p className="text-lg text-zinc-400 max-w-xl mx-auto">
              {heroContent.subtitle}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      {showBenefits && benefits.length > 0 && (
        <section className="py-12 px-4">
          <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6">
            {benefits.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 text-center"
              >
                <h3 className="text-white font-bold mb-2">{item.title}</h3>
                <p className="text-zinc-400 text-sm">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* Form */}
      <section className="py-12 px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-2xl mx-auto"
        >
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 md:p-8">
            <h2 className="text-2xl font-bold text-white mb-6">Formulario de Solicitud</h2>
            
            {loadingConfig ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="animate-spin text-[#D4AF37]" size={32} />
              </div>
            ) : formConfig && formConfig.fields?.length > 0 ? (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-4">
                  {formConfig.fields.map(field => renderField(field))}
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#D4AF37] hover:bg-[#B4941F] text-black font-bold py-6"
                >
                  {loading ? (
                    <Loader2 className="animate-spin" size={20} />
                  ) : (
                    <>
                      <Send size={18} className="mr-2" />
                      Enviar Solicitud
                    </>
                  )}
                </Button>
              </form>
            ) : (
              <div className="text-center py-12">
                <FileText className="mx-auto text-zinc-600 mb-4" size={48} />
                <p className="text-zinc-400">
                  El formulario de solicitud aún no está configurado.
                </p>
                <p className="text-zinc-500 text-sm mt-2">
                  Por favor, contactanos directamente para más información.
                </p>
              </div>
            )}
          </div>
        </motion.div>
      </section>

      <Footer />
    </div>
  );
};

export default ProducerApplicationPage;
